import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
  FileText,
  Check,
  MessageCircle,
  Send,
  Sparkles,
  Users,
  Calendar,
  Briefcase,
} from "lucide-react";
import "./ExplainerVideo.css";

/* ---------- Scene timing (seconds) — must match the narration ---------- */
const SCENES = [
  { id: 1, start: 0, end: 10, key: "problem" },
  { id: 2, start: 10, end: 16, key: "intro" },
  { id: 3, start: 16, end: 23, key: "resume" },
  { id: 4, start: 23, end: 33, key: "human-apps" },
  { id: 5, start: 33, end: 42, key: "automation" },
  { id: 6, start: 42, end: 49, key: "whatsapp" },
  { id: 7, start: 49, end: 57, key: "results" },
  { id: 8, start: 57, end: 62, key: "cta" },
];

const TOTAL = 62;

/* Company logos used in Scene 4 — simple color tiles with company initials */
const COMPANIES = [
  { name: "Google", bg: "#4285F4", initial: "G" },
  { name: "Meta", bg: "#1877F2", initial: "M" },
  { name: "Amazon", bg: "#FF9900", initial: "A" },
  { name: "Microsoft", bg: "#00A4EF", initial: "M" },
  { name: "Apple", bg: "#111", initial: "" },
  { name: "Netflix", bg: "#E50914", initial: "N" },
  { name: "Uber", bg: "#000", initial: "U" },
  { name: "Airbnb", bg: "#FF5A5F", initial: "A" },
  { name: "Stripe", bg: "#635BFF", initial: "S" },
  { name: "Spotify", bg: "#1DB954", initial: "S" },
  { name: "Adobe", bg: "#FA0F00", initial: "A" },
  { name: "Salesforce", bg: "#00A1E0", initial: "SF" },
];

/* ---------- Animated avatar — reusable SVG illustration ---------- */
const Avatar = ({ mood = "neutral", size = 140 }) => {
  const skinColor = "#FFD4B8";
  const hairColor = "#2D1B12";
  const shirtColor =
    mood === "stressed"
      ? "#64748B"
      : mood === "happy"
      ? "#1D9E75"
      : "#475569";
  return (
    <svg
      viewBox="0 0 140 160"
      width={size}
      height={size * 1.14}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Shirt */}
      <path
        d="M 25 160 L 30 110 Q 70 100 110 110 L 115 160 Z"
        fill={shirtColor}
      />
      {/* Neck */}
      <rect x="60" y="85" width="20" height="20" fill={skinColor} />
      {/* Head */}
      <circle cx="70" cy="55" r="32" fill={skinColor} />
      {/* Hair */}
      <path
        d="M 40 45 Q 40 20 70 20 Q 100 20 100 48 Q 95 35 70 32 Q 50 35 42 48 Z"
        fill={hairColor}
      />
      {/* Eyes */}
      {mood === "stressed" ? (
        <>
          <path d="M 55 55 L 62 58" stroke="#2D1B12" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 85 55 L 78 58" stroke="#2D1B12" strokeWidth="2.5" strokeLinecap="round" />
        </>
      ) : mood === "happy" ? (
        <>
          <path d="M 55 57 Q 58 54 61 57" stroke="#2D1B12" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M 79 57 Q 82 54 85 57" stroke="#2D1B12" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>
      ) : (
        <>
          <circle cx="58" cy="57" r="2.2" fill="#2D1B12" />
          <circle cx="82" cy="57" r="2.2" fill="#2D1B12" />
        </>
      )}
      {/* Mouth */}
      {mood === "stressed" && (
        <path
          d="M 62 73 Q 70 69 78 73"
          stroke="#2D1B12"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
      )}
      {mood === "happy" && (
        <path
          d="M 60 70 Q 70 80 80 70"
          stroke="#2D1B12"
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
        />
      )}
      {mood === "neutral" && (
        <line x1="63" y1="72" x2="77" y2="72" stroke="#2D1B12" strokeWidth="2" strokeLinecap="round" />
      )}
    </svg>
  );
};

/* ---------- Scene Components ---------- */

/* Scene 1 — The Problem: stressed avatar, rejection emails piling up */
function SceneProblem() {
  const emails = Array.from({ length: 8 });
  return (
    <div className="scene scene-problem">
      <div className="scene-bg-pattern" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="scene-avatar-block"
      >
        <Avatar mood="stressed" size={160} />
        <div className="laptop-prop">
          <div className="laptop-screen">
            {[1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                className="job-line"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0.3] }}
                transition={{ duration: 1, delay: i * 0.2, repeat: Infinity, repeatDelay: 3 }}
              />
            ))}
          </div>
          <div className="laptop-base" />
        </div>
      </motion.div>

      <div className="email-cascade">
        {emails.map((_, i) => (
          <motion.div
            key={i}
            className="rejection-email"
            initial={{ opacity: 0, y: -40, x: i * 18 - 60, rotate: i * 4 - 16 }}
            animate={{
              opacity: [0, 1, 1, 0.6],
              y: [-40, 0, 10, 20],
            }}
            transition={{ duration: 2, delay: i * 0.4 }}
          >
            <span className="rejection-x">✕</span>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="stat-float"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 2 }}
      >
        <span className="stat-float-num">300</span>
        <span className="stat-float-lbl">applications sent</span>
      </motion.div>
      <motion.div
        className="stat-float stat-float-alt"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 3.5 }}
      >
        <span className="stat-float-num stat-red">2</span>
        <span className="stat-float-lbl">responses</span>
      </motion.div>
    </div>
  );
}

/* Scene 2 — Introduction: BulkJobApply team appears */
function SceneIntro() {
  return (
    <div className="scene scene-intro">
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: "spring" }}
        className="brand-mark"
      >
        <Sparkles className="brand-icon" />
        <span className="brand-name">
          Bulk<span className="brand-accent">JobApply</span>
        </span>
      </motion.div>

      <div className="team-row">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="team-member"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 + i * 0.2 }}
          >
            <Avatar mood="happy" size={100} />
            <span className="team-label">
              {i === 0 ? "Resume Specialist" : i === 1 ? "Assistant" : "Network Bot"}
            </span>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="intro-tagline"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        Humans <span className="tagline-plus">+</span> Automation, working together.
      </motion.div>
    </div>
  );
}

/* Scene 3 — Resume rebuild */
function SceneResume() {
  return (
    <div className="scene scene-resume">
      <motion.div
        className="resume-card resume-before"
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="resume-tag bad-tag">BEFORE</div>
        <FileText className="resume-icon" />
        <div className="resume-line resume-line-gray" />
        <div className="resume-line resume-line-gray short" />
        <div className="resume-line resume-line-gray" />
        <div className="resume-reject">
          <span>✕</span> ATS rejected
        </div>
      </motion.div>

      <motion.div
        className="resume-arrow"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6, duration: 0.4 }}
      >
        →
      </motion.div>

      <motion.div
        className="resume-card resume-after"
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 1 }}
      >
        <div className="resume-tag good-tag">AFTER</div>
        <FileText className="resume-icon resume-icon-good" />
        <div className="resume-line resume-line-highlight" />
        <div className="resume-line resume-line-gray short" />
        <div className="resume-line resume-line-highlight" />
        <motion.div
          className="resume-approve"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2, duration: 0.3 }}
        >
          <Check className="w-4 h-4" /> ATS Optimized
        </motion.div>
      </motion.div>
    </div>
  );
}

/* Scene 4 — 780 human applications to real companies */
function SceneHumanApps({ sceneTime }) {
  const progress = Math.min(sceneTime / 9, 1);
  const counter = Math.round(progress * 780);

  return (
    <div className="scene scene-human-apps">
      <div className="human-apps-left">
        <Avatar mood="happy" size={120} />
        <div className="human-role">
          <Users className="w-4 h-4" />
          Human Assistant
        </div>
      </div>

      <div className="human-apps-middle">
        <motion.div
          className="big-counter"
          key={counter}
          initial={{ scale: 1 }}
        >
          {counter.toLocaleString()}
        </motion.div>
        <div className="counter-label">tailored applications / month</div>
        <motion.div
          className="counter-sub"
          initial={{ opacity: 0 }}
          animate={{ opacity: sceneTime > 4 ? 1 : 0 }}
        >
          On real career pages · 1 of 50 applicants, not 1000
        </motion.div>
      </div>

      <div className="companies-grid">
        {COMPANIES.map((c, i) => (
          <motion.div
            key={c.name}
            className="company-tile"
            style={{ background: c.bg }}
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            transition={{ delay: i * 0.08, type: "spring", stiffness: 180 }}
          >
            {c.initial}
            <motion.div
              className="company-check"
              initial={{ opacity: 0 }}
              animate={{ opacity: sceneTime > 3 + i * 0.15 ? 1 : 0 }}
            >
              <Check className="w-3 h-3" />
            </motion.div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* Scene 5 — ChillApply + ChillRefer automation split */
function SceneAutomation({ sceneTime }) {
  return (
    <div className="scene scene-automation">
      <motion.div
        className="auto-panel auto-left"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="auto-label">
          <Sparkles className="w-4 h-4" /> ChillApply
        </div>
        <div className="auto-num">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            4,000+
          </motion.span>
        </div>
        <div className="auto-desc">easy-apply jobs</div>
        <div className="auto-pulse-row">
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={i}
              className="auto-pulse"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1, delay: i * 0.15, repeat: Infinity }}
            />
          ))}
        </div>
      </motion.div>

      <motion.div
        className="auto-panel auto-right"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="auto-label">
          <Users className="w-4 h-4" /> ChillRefer
        </div>
        <div className="auto-num">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            560
          </motion.span>
        </div>
        <div className="auto-desc">recruiter connections</div>
        <div className="network-graph">
          <div className="network-center" />
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i / 8) * Math.PI * 2;
            const x = Math.cos(angle) * 50;
            const y = Math.sin(angle) * 50;
            return (
              <motion.div
                key={i}
                className="network-node"
                style={{ left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)` }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1 + i * 0.08, type: "spring" }}
              />
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

/* Scene 6 — WhatsApp chat in real-time */
function SceneWhatsApp({ sceneTime }) {
  const messages = [
    { t: 0.5, from: "team", text: "Applied to Google (SWE II)" },
    { t: 1.8, from: "team", text: "Resume tailored for Meta" },
    { t: 3, from: "client", text: "Amazing 👏" },
    { t: 4.2, from: "team", text: "📞 Callback from Stripe!" },
  ];
  return (
    <div className="scene scene-whatsapp">
      <div className="phone-frame">
        <div className="phone-notch" />
        <div className="phone-screen">
          <div className="chat-header">
            <div className="chat-avatar-sm">
              <Briefcase className="w-3.5 h-3.5" />
            </div>
            <div>
              <div className="chat-title">BulkJobApply Team</div>
              <div className="chat-status">online · always</div>
            </div>
          </div>
          <div className="chat-body">
            {messages.map((m) => (
              <motion.div
                key={m.t}
                className={`chat-msg chat-${m.from}`}
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{
                  opacity: sceneTime > m.t ? 1 : 0,
                  y: sceneTime > m.t ? 0 : 10,
                  scale: sceneTime > m.t ? 1 : 0.9,
                }}
                transition={{ duration: 0.3 }}
              >
                {m.text}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Scene 7 — Results: calendar fills with interviews + stats */
function SceneResults({ sceneTime }) {
  return (
    <div className="scene scene-results">
      <div className="results-left">
        <div className="mini-calendar">
          <div className="mc-header">
            <Calendar className="w-4 h-4" />
            Next 2 Weeks
          </div>
          <div className="mc-grid">
            {Array.from({ length: 14 }).map((_, i) => {
              const isInterview = [2, 4, 7, 9, 11].includes(i);
              const delay = 0.3 + i * 0.08;
              return (
                <motion.div
                  key={i}
                  className={`mc-cell ${isInterview ? "mc-has" : ""}`}
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay }}
                >
                  {isInterview && <div className="mc-dot" />}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="results-right">
        {[
          { big: "93%", sub: "land a job" },
          { big: "500+", sub: "success stories" },
          { big: "14d", sub: "first interview" },
        ].map((s, i) => (
          <motion.div
            key={s.big}
            className="result-stat"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1 + i * 0.3, duration: 0.4 }}
          >
            <div className="result-big">{s.big}</div>
            <div className="result-sub">{s.sub}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* Scene 8 — CTA */
function SceneCta() {
  return (
    <div className="scene scene-cta">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="cta-headline"
      >
        Ready to stop applying
        <br />
        <span className="cta-accent">and start interviewing?</span>
      </motion.div>
      <motion.div
        className="cta-row"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="cta-pill">Plans from $399</div>
        <div className="cta-pill cta-pill-accent">
          Guaranteed: $750 + $750
        </div>
      </motion.div>
      <motion.a
        href="https://app.bulkjobapply.com/register"
        target="_blank"
        rel="noopener noreferrer"
        className="cta-btn-big"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, type: "spring" }}
        data-testid="explainer-cta-btn"
      >
        Book your free demo
        <Send className="w-4 h-4" />
      </motion.a>
    </div>
  );
}

/* ---------- Overlay subtitle mapping ---------- */
const OVERLAY = [
  { start: 0, end: 4.5, text: "300 applications. 2 responses." },
  { start: 4.5, end: 10, text: "You're not doing it wrong. You're doing it alone." },
  { start: 10, end: 16, text: "Meet your team. Humans plus automation." },
  { start: 16, end: 23, text: "Step 1. We rebuild your resume, ATS-optimized." },
  { start: 23, end: 33, text: "Step 2. 780 tailored applications, every month." },
  { start: 33, end: 42, text: "Step 3. ChillApply and ChillRefer handle 4,560 more." },
  { start: 42, end: 49, text: "Step 4. Everything on WhatsApp. Live." },
  { start: 49, end: 57, text: "Step 5. Interviews land. 93% success rate." },
  { start: 57, end: 62, text: "" }, // CTA scene has own headline
];

/* ---------- Main Component ---------- */
export default function ExplainerVideo({ recordMode = false } = {}) {
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const recordTimerRef = useRef(null);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [started, setStarted] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;
  const audioUrl = `${API}/api/static/explainer.mp3`;

  const activeScene = SCENES.find((s) => time >= s.start && time < s.end) || SCENES[SCENES.length - 1];
  const sceneTime = time - activeScene.start;
  const activeOverlay = OVERLAY.find((o) => time >= o.start && time < o.end);

  /* RAF loop for smooth progress */
  const tick = useCallback(() => {
    if (audioRef.current) {
      setTime(audioRef.current.currentTime);
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (recordMode) return; // record mode drives time via its own interval
    if (playing) {
      rafRef.current = requestAnimationFrame(tick);
    } else if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, tick, recordMode]);

  /* Record mode: drive time deterministically without relying on audio playback. */
  useEffect(() => {
    if (!recordMode) return;
    setStarted(true);
    setPlaying(true);
    const startTs = performance.now();
    recordTimerRef.current = setInterval(() => {
      const elapsed = (performance.now() - startTs) / 1000;
      setTime(elapsed);
      if (elapsed >= TOTAL + 0.5) {
        clearInterval(recordTimerRef.current);
        recordTimerRef.current = null;
        window.__explainerFinished = true;
      }
    }, 33); // ~30fps
    window.__explainerStarted = true;
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    };
  }, [recordMode]);

  const handlePlay = () => {
    if (!audioRef.current) return;
    audioRef.current.play().catch(() => {});
    setPlaying(true);
    setStarted(true);
  };

  const handlePause = () => {
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
  };

  const handleRestart = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    setTime(0);
    audioRef.current.play().catch(() => {});
    setPlaying(true);
    setStarted(true);
  };

  const handleToggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !muted;
    setMuted(!muted);
  };

  const handleEnded = () => {
    setPlaying(false);
    setTime(TOTAL);
  };

  const progressPct = Math.min((time / TOTAL) * 100, 100);

  return (
    <section className="explainer-section" data-testid="explainer-section">
      <div className="explainer-wrap">
        <div className="explainer-header">
          <h2 className="explainer-title">
            Here's exactly how we <span className="explainer-title-accent">land you a job.</span>
          </h2>
          <p className="explainer-sub">
            A real human team + smart automation, running your job search full-time.
          </p>
        </div>

        <div
          className="explainer-player"
          data-testid="explainer-player"
          role="region"
          aria-label="BulkJobApply explainer video"
        >
          {/* Scene stage */}
          <div className="explainer-stage">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeScene.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="scene-wrapper"
              >
                {activeScene.key === "problem" && <SceneProblem sceneTime={sceneTime} />}
                {activeScene.key === "intro" && <SceneIntro sceneTime={sceneTime} />}
                {activeScene.key === "resume" && <SceneResume sceneTime={sceneTime} />}
                {activeScene.key === "human-apps" && <SceneHumanApps sceneTime={sceneTime} />}
                {activeScene.key === "automation" && <SceneAutomation sceneTime={sceneTime} />}
                {activeScene.key === "whatsapp" && <SceneWhatsApp sceneTime={sceneTime} />}
                {activeScene.key === "results" && <SceneResults sceneTime={sceneTime} />}
                {activeScene.key === "cta" && <SceneCta sceneTime={sceneTime} />}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Play overlay (before user starts) — rich cover for higher conversion */}
          {!started && !recordMode && (
            <button
              className="explainer-play-overlay explainer-cover"
              onClick={handlePlay}
              data-testid="explainer-play-overlay"
              aria-label="Play explainer video"
            >
              <div className="explainer-cover-bg" aria-hidden="true">
                <div className="explainer-cover-orb explainer-cover-orb-1" />
                <div className="explainer-cover-orb explainer-cover-orb-2" />
                <div className="explainer-cover-grid" />
              </div>

              <div className="explainer-cover-content">
                <span className="explainer-cover-pill">
                  <span className="explainer-cover-pill-dot" />
                  60-second explainer
                </span>
                <h3 className="explainer-cover-title">
                  How BulkJobApply gets you<br />
                  <span className="explainer-cover-highlight">5+ interview callbacks in 30 days</span>
                </h3>
                <p className="explainer-cover-sub">
                  The exact system 500+ people used to land jobs at Airbnb, Stripe, Deloitte, Amazon, EY.
                </p>

                <div className="play-circle">
                  <Play className="play-icon" fill="currentColor" />
                </div>
                <div className="play-label">Tap to play · Sound on 🔊</div>

                <div className="explainer-cover-logos" aria-label="Companies our clients got hired at">
                  <span>Airbnb</span>
                  <span aria-hidden="true">·</span>
                  <span>Stripe</span>
                  <span aria-hidden="true">·</span>
                  <span>Deloitte</span>
                  <span aria-hidden="true">·</span>
                  <span>Amazon</span>
                  <span aria-hidden="true">·</span>
                  <span>EY</span>
                </div>
              </div>
            </button>
          )}

          {/* Controls */}
          {started && !recordMode && (
            <div className="explainer-controls" data-testid="explainer-controls">
              <button
                className="ctrl-btn"
                onClick={playing ? handlePause : handlePlay}
                data-testid={playing ? "pause-btn" : "play-btn"}
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" fill="currentColor" />}
              </button>

              <div className="progress-wrap">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
                <span className="time-lbl">
                  {Math.floor(time)}s / {TOTAL}s
                </span>
              </div>

              <button
                className="ctrl-btn"
                onClick={handleRestart}
                data-testid="restart-btn"
                aria-label="Restart"
              >
                <RotateCcw className="w-4 h-4" />
              </button>

              <button
                className="ctrl-btn"
                onClick={handleToggleMute}
                data-testid="mute-btn"
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            </div>
          )}

          <audio
            ref={audioRef}
            src={audioUrl}
            preload="auto"
            onEnded={handleEnded}
            data-testid="explainer-audio"
          />
        </div>

        {/* Caption bar — BELOW the player, never overlaps scene content */}
        <div
          className={`explainer-caption ${started && activeOverlay && activeOverlay.text ? 'explainer-caption-active' : ''}`}
          data-testid="explainer-caption"
          aria-live="polite"
        >
          {started && activeOverlay && activeOverlay.text ? (
            <motion.span
              key={activeOverlay.text}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {activeOverlay.text}
            </motion.span>
          ) : (
            <span className="explainer-caption-hint">
              {!started ? 'Tap play to start. Sound on 🔊' : ''}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
